/**
 * MedicationMAR — Electronic Medication Administration Record
 *
 * CQC-compliant eMAR for admin/manager and carer views.
 * Based on Osabea Medication Monthly MAR Chart, PRN MAR, and CD Register templates.
 *
 * Legal basis:
 *   - Health & Social Care Act 2008, Regulation 12(2)(g)
 *   - NICE Guideline SC1: Managing Medicines in Care Homes
 *   - Misuse of Drugs Regulations 2001 (Controlled Drugs)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Pill, Plus, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  MinusCircle, AlertTriangle, Building2, Plane, Shield, Edit3,
  Clock, Info, TriangleAlert, Lock, Unlock, FileText, RefreshCw,
  TrendingUp, AlertCircle, Eye, EyeOff, Download,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import ControlledDrugRegister from './ControlledDrugRegister';

const API = API_BASE;

// ─── STATUS CONFIGURATION ─────────────────────────────────────────────────────
const STATUS_CONFIG = {
  given:             { label: 'Given',                   code: 'G',  icon: CheckCircle2, bg: 'bg-[#D8E6D5]', text: 'text-[#1F8F5F]', border: 'border-[#1F8F5F]/20', dot: '#1F8F5F' },
  refused:           { label: 'Refused',                 code: 'R',  icon: XCircle,      bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',      dot: '#C54B4B' },
  not_required:      { label: 'Not required',            code: 'NR', icon: MinusCircle,  bg: 'bg-slate-50',  text: 'text-slate-500',  border: 'border-slate-200',    dot: '#9CA3AF' },
  not_available:     { label: 'Out of stock',            code: 'NA', icon: AlertTriangle,bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',    dot: '#C78A12' },
  away:              { label: 'Away',                    code: 'A',  icon: Plane,        bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',     dot: '#2F6FED' },
  hospital:          { label: 'Hospital',                code: 'H',  icon: Building2,    bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',     dot: '#2F6FED' },
  self_administered: { label: 'Self-admin',              code: 'SA', icon: Shield,       bg: 'bg-[#D8E6D5]', text: 'text-[#1F8F5F]', border: 'border-[#1F8F5F]/20', dot: '#1F8F5F' },
  carer_unavailable: { label: 'Carer unavailable',       code: 'CU', icon: AlertCircle,  bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',    dot: '#C78A12' },
  error:             { label: 'Concern — see note',      code: '!',  icon: TriangleAlert,bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',      dot: '#C54B4B' },
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_ABBR    = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const ROUTE_ABBR = {
  oral: 'PO', topical: 'TOP', inhalation: 'INH', subcutaneous: 'SC',
  intramuscular: 'IM', sublingual: 'SL', buccal: 'BUC', rectal: 'PR',
  vaginal: 'PV', otic: 'AUR', ophthalmic: 'OPH', nasal: 'NAS',
  transdermal: 'TD', other: '',
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmt = (date) => new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const getDayOfWeek = (y, m, d) => new Date(y, m - 1, d).getDay();

function AdherencePill({ pct }) {
  if (!Number.isFinite(Number(pct))) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800"><AlertTriangle className="h-3 w-3" />Schedule review</span>;
  }
  const value = Number(pct);
  const color = value >= 90 ? 'bg-[#D8E6D5] text-[#1F8F5F]' : value >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}><TrendingUp className="h-3 w-3" />{value}%</span>;
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

/** Cell in the MAR grid — click to record */
function MARCell({ entry, medication, day, time, isPast, isToday, isFuture, onRecord, canRecord }) {

  if (isFuture) {
    return <div className="w-7 h-7 rounded flex items-center justify-center bg-slate-50 text-slate-200 text-[9px] font-medium select-none">—</div>;
  }

  if (entry) {
    const cfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.given;
    const Icon = cfg.icon;
    return (
      <button
        onClick={() => canRecord && onRecord({ entry, medication, day, time, mode: 'view' })}
        className={`w-7 h-7 rounded flex items-center justify-center text-[9px] font-bold ${cfg.bg} ${cfg.text} border ${cfg.border} transition-transform hover:scale-105`}
        title={`${cfg.label}${entry.carer_name ? ` — ${entry.carer_name}` : ''}${entry.is_amended ? ' (amended)' : ''}`}
      >
        {entry.is_amended ? <Edit3 className="h-3 w-3" /> : cfg.code}
      </button>
    );
  }

  if (isPast || isToday) {
    return (
      <button
        onClick={() => canRecord && onRecord({ entry: null, medication, day, time, mode: 'record' })}
        disabled={!canRecord}
        className={`w-7 h-7 rounded flex items-center justify-center border-2 border-dashed text-[9px] transition-all
          ${canRecord
            ? 'border-slate-300 hover:border-[#0F5C5E] hover:bg-[#D9F1F4]/40 cursor-pointer text-slate-400 hover:text-[#0F5C5E]'
            : 'border-slate-200 cursor-not-allowed text-slate-200'
          }
          ${isPast && !entry ? 'border-red-200 bg-red-50/30' : ''}
        `}
        title={canRecord ? 'Click to record' : 'Competency required'}
      >
        {isPast && !entry ? <span className="text-red-300">?</span> : <Plus className="h-2.5 w-2.5" />}
      </button>
    );
  }

  return null;
}

/** Record / View administration dialog */
function RecordDialog({ open, onClose, cell, onSaved, serviceUserId }) {
  const { user } = useAuth();
  const [status, setStatus] = useState('given');
  const [actualTime, setActualTime] = useState('');
  const [doseGiven, setDoseGiven] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [saving, setSaving] = useState(false);
  const [amendMode, setAmendMode] = useState(false);
  const [amendReason, setAmendReason] = useState('');

  useEffect(() => {
    if (!open || !cell) return;
    setStatus(cell.entry?.status || 'given');
    setActualTime(cell.entry?.actual_time || cell.time || '');
    setDoseGiven(cell.entry?.dose_given || cell.medication?.dose || '');
    setReasonDetail(cell.entry?.reason_detail || '');
    setAmendMode(false);
    setAmendReason('');
  }, [open, cell]);

  if (!open || !cell) return null;

  const { medication, day, time, mode, entry } = cell;
  const isViewMode = mode === 'view' && entry && !amendMode;
  const isAdmin = ['admin', 'manager', 'super_admin', 'branch_manager', 'superadmin'].includes(user?.role);
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const handleRecord = async () => {
    setSaving(true);
    try {
      const dateStr = `${cell.year}-${String(cell.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const administered = ['given', 'self_administered'].includes(status);
      const doseForRequest = administered ? doseGiven : null;
      if (amendMode && entry) {
        await axios.patch(
          `${API}/service-users/${serviceUserId}/mar-entries/${entry.id}/amend`,
          { status, actual_time: actualTime, dose_given: doseForRequest, reason_detail: reasonDetail, amendment_reason: amendReason },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('MAR entry amended');
      } else {
        await axios.post(
          `${API}/service-users/${serviceUserId}/mar-entries`,
          { medication_id: medication.id, administration_date: dateStr, scheduled_time: time, status, actual_time: actualTime, dose_given: doseForRequest, reason_detail: reasonDetail },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(STATUS_CONFIG[status]?.label || 'Recorded');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save entry');
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, ...v }));
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.given;
  const Icon = cfg.icon;

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0F5C5E] to-[#163A70] rounded-t-2xl px-6 py-5 text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-white/70 uppercase tracking-wider">
              {amendMode ? 'Amend Entry' : isViewMode ? 'MAR Entry' : 'Record Administration'}
            </span>
            <button onClick={onClose} className="text-white/60 hover:text-white text-xl leading-none">×</button>
          </div>
          <h2 className="text-lg font-bold">{medication?.medication_name}</h2>
          <p className="text-sm text-white/80">{medication?.dose} · {medication?.form} · {medication?.route && ROUTE_ABBR[medication.route] || medication?.route}</p>
          <p className="text-sm text-white/70 mt-1">
            {fmtTime(time)} · {String(day).padStart(2,'0')} {MONTH_NAMES[(cell.month||1)-1]} {cell.year}
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Covert / special instructions alert */}
          {medication?.covert && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 font-medium">Covert administration — MCA best interests decision in place. Administer as directed.</p>
            </div>
          )}
          {medication?.special_instructions && (
            <div className="flex items-start gap-2 p-3 bg-[#D9F1F4]/30 border border-[#0F5C5E]/20 rounded-lg">
              <Info className="h-4 w-4 text-[#0F5C5E] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#0F5C5E]">{medication.special_instructions}</p>
            </div>
          )}

          {/* View mode */}
          {isViewMode ? (
            <div className="space-y-3">
              <div className={`flex items-center gap-3 p-3 rounded-xl border ${cfg.bg} ${cfg.border}`}>
                <Icon className={`h-5 w-5 ${cfg.text}`} />
                <div>
                  <p className={`text-sm font-semibold ${cfg.text}`}>{cfg.label}</p>
                  {entry.actual_time && <p className="text-xs text-slate-500">Given at {fmtTime(entry.actual_time)}</p>}
                  {entry.reason_detail && <p className="text-xs text-slate-500">{entry.reason_detail}</p>}
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Recorded by {entry.carer_name}</span>
                {entry.is_amended && <Badge className="bg-amber-100 text-amber-700 text-xs"><Edit3 className="h-3 w-3 mr-1" />Amended</Badge>}
              </div>
              {isAdmin && (
                <button onClick={() => setAmendMode(true)} className="w-full text-center text-xs text-[#0F5C5E] hover:underline py-1">
                  Amend this entry (audit trail preserved)
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Status selection */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Administration status *</label>
                <div className="grid grid-cols-2 gap-2">
                  {statusOptions.map(s => {
                    const SIcon = s.icon;
                    return (
                      <button
                        key={s.value}
                        onClick={() => setStatus(s.value)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-left transition-all text-xs font-medium
                          ${status === s.value
                            ? `${s.bg} ${s.text} ${s.border} border-2`
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                          }`}
                      >
                        <SIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Actual time */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Time administered</label>
                <input
                  type="time"
                  value={actualTime}
                  onChange={e => setActualTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E] focus:border-[#0F5C5E]"
                />
              </div>

              {/* Dose given (if different) */}
              {status === 'given' || status === 'self_administered' ? (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Dose given (if different from prescribed)</label>
                  <input
                    type="text"
                    value={doseGiven}
                    onChange={e => setDoseGiven(e.target.value)}
                    placeholder={medication?.dose}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E] focus:border-[#0F5C5E]"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {status === 'refused' ? 'Reason for refusal' : 'Notes'} {['refused','error'].includes(status) ? '*' : ''}
                  </label>
                  <textarea
                    value={reasonDetail}
                    onChange={e => setReasonDetail(e.target.value)}
                    rows={2}
                    placeholder={status === 'refused' ? 'State the reason the service user refused...' : 'Any relevant notes...'}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none focus:ring-1 focus:ring-[#0F5C5E] focus:border-[#0F5C5E]"
                  />
                </div>
              )}

              {/* Amend reason */}
              {amendMode && (
                <div>
                  <label className="block text-xs font-semibold text-red-700 mb-1">Reason for amendment * (required — visible in audit trail)</label>
                  <textarea
                    value={amendReason}
                    onChange={e => setAmendReason(e.target.value)}
                    rows={2}
                    placeholder="Clearly state why this entry is being amended..."
                    className="w-full px-3 py-2 rounded-lg border border-red-300 text-sm resize-none focus:ring-1 focus:ring-red-500"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isViewMode && (
          <div className="px-6 pb-6 flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button
              onClick={handleRecord}
              disabled={saving || (amendMode && !amendReason.trim()) || (['refused','error'].includes(status) && !reasonDetail.trim())}
              className="flex-1 bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white"
            >
              {saving ? 'Saving…' : amendMode ? 'Save Amendment' : 'Confirm & Sign'}
            </Button>
          </div>
        )}
        {isViewMode && (
          <div className="px-6 pb-6">
            <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Add / Edit Medication dialog */
function MedicationDialog({ open, onClose, onSaved, serviceUserId, editingMed }) {
  const [form, setForm] = useState({
    medication_name: '', generic_name: '', dose: '', form: 'tablet', route: 'oral',
    frequency: 'once daily (OD)', times: ['08:00'], indication: '', prescriber: '',
    gp_name: '', start_date: todayStr(), end_date: '', special_instructions: '',
    is_prn: false, is_controlled_drug: false, cd_schedule: '', max_prn_dose_24h: '',
    prn_min_interval_minutes: '', prn_max_administrations_24h: '', prn_review_after_minutes: '',
    covert: false, self_administered: false,
  });
  const [saving, setSaving] = useState(false);
  const [timeInput, setTimeInput] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editingMed) {
      setForm({ ...editingMed, times: editingMed.times || ['08:00'] });
    } else {
      setForm({
        medication_name: '', generic_name: '', dose: '', form: 'tablet', route: 'oral',
        frequency: 'once daily (OD)', times: ['08:00'], indication: '', prescriber: '',
        gp_name: '', start_date: todayStr(), end_date: '', special_instructions: '',
        is_prn: false, is_controlled_drug: false, cd_schedule: '', max_prn_dose_24h: '',
        prn_min_interval_minutes: '', prn_max_administrations_24h: '', prn_review_after_minutes: '',
        covert: false, self_administered: false,
      });
    }
  }, [open, editingMed]);

  if (!open) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');

  const addTime = () => {
    if (timeInput && !form.times.includes(timeInput)) {
      set('times', [...form.times, timeInput].sort());
      setTimeInput('');
    }
  };
  const removeTime = (t) => set('times', form.times.filter(x => x !== t));

  const handleSave = async () => {
    if (!form.medication_name.trim() || !form.dose.trim()) {
      toast.error('Medication name and dose are required');
      return;
    }
    if (form.times.length === 0 && !form.is_prn) {
      toast.error('At least one administration time is required for regular medications');
      return;
    }
    if (form.is_prn && (
      !form.indication?.trim()
      || !form.special_instructions?.trim()
      || !form.max_prn_dose_24h?.trim()
      || !Number(form.prn_min_interval_minutes)
      || !Number(form.prn_max_administrations_24h)
      || !Number(form.prn_review_after_minutes)
    )) {
      toast.error('Complete the PRN indication, person-specific instructions, max dose, minimum interval, 24-hour limit and effectiveness review time');
      return;
    }
    const requestPayload = {
      ...form,
      frequency: form.is_prn ? 'as required (PRN)' : form.frequency,
      max_prn_dose_24h: form.is_prn ? form.max_prn_dose_24h : null,
      prn_min_interval_minutes: form.is_prn ? Number(form.prn_min_interval_minutes) : null,
      prn_max_administrations_24h: form.is_prn ? Number(form.prn_max_administrations_24h) : null,
      prn_review_after_minutes: form.is_prn ? Number(form.prn_review_after_minutes) : null,
    };
    setSaving(true);
    try {
      if (editingMed) {
        await axios.patch(`${API}/service-users/${serviceUserId}/medications/${editingMed.id}`, requestPayload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Medication updated');
      } else {
        await axios.post(`${API}/service-users/${serviceUserId}/medications`, requestPayload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Medication added to prescription list');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save medication');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E] focus:border-[#0F5C5E] bg-white';
  const labelCls = 'block text-xs font-semibold text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#0F5C5E] to-[#163A70] rounded-t-2xl px-6 py-5 text-white sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-white/70 uppercase tracking-wider">Prescription List</p>
              <h2 className="text-lg font-bold">{editingMed ? 'Update Medication' : 'Add Medication'}</h2>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white text-xl">×</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Medication name (proprietary) *</label>
              <input className={inputCls} value={form.medication_name} onChange={e => set('medication_name', e.target.value)} placeholder="e.g. Amlodipine" />
            </div>
            <div>
              <label className={labelCls}>Generic / INN name</label>
              <input className={inputCls} value={form.generic_name} onChange={e => set('generic_name', e.target.value)} placeholder="e.g. amlodipine besylate" />
            </div>
            <div>
              <label className={labelCls}>Dose *</label>
              <input className={inputCls} value={form.dose} onChange={e => set('dose', e.target.value)} placeholder="e.g. 5mg" />
            </div>
            <div>
              <label className={labelCls}>Form</label>
              <select className={inputCls} value={form.form} onChange={e => set('form', e.target.value)}>
                {['tablet','capsule','liquid','suspension','syrup','patch','cream','ointment','gel','drops','inhaler','nebuliser','injection','suppository','pessary','sublingual','buccal','powder','other'].map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Route</label>
              <select className={inputCls} value={form.route} onChange={e => set('route', e.target.value)}>
                {['oral','topical','inhalation','subcutaneous','intramuscular','sublingual','buccal','rectal','vaginal','otic','ophthalmic','nasal','transdermal','other'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>

          {/* Frequency & Times */}
          <div>
            <label className={labelCls}>Frequency</label>
            <select className={inputCls} value={form.frequency} onChange={e => set('frequency', e.target.value)}>
              {['once daily (OD)','twice daily (BD)','three times daily (TDS)','four times daily (QDS)','five times daily','every morning (AM)','every night (nocte)','alternate days','weekly','fortnightly','monthly','as required (PRN)','with meals','before meals','after meals','with morning meal','with evening meal'].map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
          {!form.is_prn && (
            <div>
              <label className={labelCls}>Administration times</label>
              <div className="flex gap-2 mb-2">
                <input type="time" value={timeInput} onChange={e => setTimeInput(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-1 focus:ring-[#0F5C5E]" />
                <Button type="button" variant="outline" size="sm" onClick={addTime}>Add time</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.times.map(t => (
                  <span key={t} className="flex items-center gap-1 px-2 py-1 bg-[#D9F1F4]/50 text-[#0F5C5E] rounded-lg text-xs font-medium border border-[#0F5C5E]/20">
                    {fmtTime(t)}
                    <button onClick={() => removeTime(t)} className="text-[#0F5C5E]/60 hover:text-[#0F5C5E] ml-1">×</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Clinical */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Indication (what it is prescribed for)</label>
              <input className={inputCls} value={form.indication} onChange={e => set('indication', e.target.value)} placeholder="e.g. Hypertension" />
            </div>
            <div>
              <label className={labelCls}>Prescriber</label>
              <input className={inputCls} value={form.prescriber} onChange={e => set('prescriber', e.target.value)} placeholder="Dr. Smith" />
            </div>
            <div>
              <label className={labelCls}>GP surgery</label>
              <input className={inputCls} value={form.gp_name} onChange={e => set('gp_name', e.target.value)} placeholder="Manor Road Surgery" />
            </div>
            <div>
              <label className={labelCls}>Start date</label>
              <input type="date" className={inputCls} value={form.start_date} onChange={e => set('start_date', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>End / review date (optional)</label>
              <input type="date" className={inputCls} value={form.end_date} onChange={e => set('end_date', e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Special instructions</label>
              <textarea className={inputCls} rows={2} value={form.special_instructions} onChange={e => set('special_instructions', e.target.value)} placeholder="e.g. Take with food. Monitor for dizziness." />
            </div>
          </div>

          {/* Flags */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'is_prn', label: 'PRN (as required)', desc: 'Not on regular schedule — record each use separately' },
              { key: 'is_controlled_drug', label: 'Controlled Drug', desc: 'Requires dual-witness CD register entry' },
              { key: 'covert', label: 'Covert administration', desc: 'MCA best interests decision must be on file' },
              { key: 'self_administered', label: 'Self-administered', desc: 'Service user administers independently' },
            ].map(f => (
              <label key={f.key} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${form[f.key] ? 'border-[#0F5C5E] bg-[#D9F1F4]/20' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <input
                  type="checkbox"
                  checked={form[f.key]}
                  onChange={e => {
                    const checked = e.target.checked;
                    if (f.key === 'is_prn') {
                      setForm(current => ({
                        ...current,
                        is_prn: checked,
                        frequency: checked ? 'as required (PRN)' : (current.frequency === 'as required (PRN)' ? 'once daily (OD)' : current.frequency),
                        times: checked ? [] : (current.times?.length ? current.times : ['08:00']),
                      }));
                    } else {
                      set(f.key, checked);
                    }
                  }}
                  className="mt-0.5 accent-[#0F5C5E]"
                />
                <div>
                  <p className="text-xs font-semibold text-slate-800">{f.label}</p>
                  <p className="text-xs text-slate-500">{f.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {form.is_prn && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
              <div>
                <p className="text-xs font-semibold text-amber-900">PRN safety protocol</p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  CareOps also requires the indication above and person-specific Special instructions describing signs/symptoms, appropriate non-medicine support and when to escalate/review.
                </p>
              </div>
              <div>
                <label className={labelCls}>Maximum dose in 24 hours *</label>
                <input className={inputCls} value={form.max_prn_dose_24h || ''} onChange={e => set('max_prn_dose_24h', e.target.value)} placeholder="e.g. 2 tablets / 1g in 24 hours" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Minimum interval *</label>
                  <input type="number" min="1" max="1440" className={inputCls} value={form.prn_min_interval_minutes || ''} onChange={e => set('prn_min_interval_minutes', e.target.value ? Number(e.target.value) : '')} placeholder="240" />
                  <p className="text-[10px] text-slate-500 mt-1">Minutes between doses</p>
                </div>
                <div>
                  <label className={labelCls}>Max administrations *</label>
                  <input type="number" min="1" max="24" className={inputCls} value={form.prn_max_administrations_24h || ''} onChange={e => set('prn_max_administrations_24h', e.target.value ? Number(e.target.value) : '')} placeholder="4" />
                  <p className="text-[10px] text-slate-500 mt-1">Rolling 24 hours</p>
                </div>
                <div>
                  <label className={labelCls}>Review effect after *</label>
                  <input type="number" min="1" max="1440" className={inputCls} value={form.prn_review_after_minutes || ''} onChange={e => set('prn_review_after_minutes', e.target.value ? Number(e.target.value) : '')} placeholder="60" />
                  <p className="text-[10px] text-slate-500 mt-1">Minutes after giving</p>
                </div>
              </div>
            </div>
          )}
          {form.is_controlled_drug && (
            <div>
              <label className={labelCls}>CD Schedule</label>
              <select className={inputCls} value={form.cd_schedule} onChange={e => set('cd_schedule', e.target.value)}>
                <option value="">Select schedule</option>
                {['Schedule 2','Schedule 3','Schedule 4','Schedule 5'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
            {saving ? 'Saving…' : editingMed ? 'Update Medication' : 'Add to Prescription List'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** PRN History list */
function PRNHistory({ serviceUserId, prnMeds, canRecord }) {
  const localNow = () => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordOpen, setRecordOpen] = useState(false);
  const [prnForm, setPrnForm] = useState({
    medication_id: '', administered_at: localNow(), dose_given: '', indication: '', notes: '',
  });
  const [outcomeEntry, setOutcomeEntry] = useState(null);
  const [outcomeForm, setOutcomeForm] = useState({ outcome: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const recordablePrnMeds = prnMeds.filter(med => !med.is_controlled_drug);

  const load = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    try {
      const res = await axios.get(`${API}/service-users/${serviceUserId}/prn-history`, { headers: { Authorization: `Bearer ${token}` } });
      setEntries(res.data || []);
    } catch {
      toast.error('Could not load PRN history');
    } finally {
      setLoading(false);
    }
  }, [serviceUserId]);

  useEffect(() => { load(); }, [load]);

  const errorText = (err, fallback) => {
    const detail = err.response?.data?.detail;
    return typeof detail === 'string' ? detail : detail?.message || fallback;
  };

  const handleRecord = async () => {
    if (!prnForm.medication_id || !prnForm.dose_given.trim() || !prnForm.indication.trim()) {
      toast.error('Medication, dose given, and reason for giving are required');
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/prn-entries`,
        { ...prnForm, administered_at: new Date(prnForm.administered_at).toISOString() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('PRN administration recorded — effectiveness follow-up is now due');
      setRecordOpen(false);
      setPrnForm({ medication_id: '', administered_at: localNow(), dose_given: '', indication: '', notes: '' });
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not record PRN'));
    } finally {
      setSaving(false);
    }
  };

  const handleOutcome = async () => {
    if (!outcomeEntry || !outcomeForm.outcome) {
      toast.error('Select an effectiveness outcome');
      return;
    }
    if (['partially_effective', 'no_effect'].includes(outcomeForm.outcome) && !outcomeForm.notes.trim()) {
      toast.error('Add follow-up notes for a partial or no-effect outcome');
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/prn-entries/${outcomeEntry.id}/outcome`,
        { outcome: outcomeForm.outcome, observed_at: new Date().toISOString(), notes: outcomeForm.notes },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('PRN effectiveness review recorded');
      setOutcomeEntry(null);
      setOutcomeForm({ outcome: '', notes: '' });
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not record PRN outcome'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">PRN Medication Log</h3>
          <p className="text-xs text-slate-500">Record why it was given first, then record effectiveness as a separate follow-up.</p>
        </div>
        {canRecord && recordablePrnMeds.length > 0 && (
          <Button size="sm" onClick={() => setRecordOpen(true)} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
            <Plus className="h-3.5 w-3.5 mr-1" />Record PRN
          </Button>
        )}
      </div>

      {prnMeds.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {prnMeds.map(med => {
            const todayCount = entries.filter(e => e.medication_id === med.id && e.administered_at?.startsWith(todayStr())).length;
            const protocolComplete = Boolean(
              med.indication && med.special_instructions
              && med.max_prn_dose_24h && med.prn_min_interval_minutes
              && med.prn_max_administrations_24h && med.prn_review_after_minutes
            );
            return (
              <div key={med.id} className={`p-4 rounded-xl border bg-white shadow-sm ${protocolComplete ? 'border-slate-200' : 'border-red-200'}`}>
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{med.medication_name}</p>
                    <p className="text-xs text-slate-500">{med.dose} · {med.form}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 text-xs">PRN</Badge>
                </div>
                {med.indication && <p className="text-xs text-slate-600 mt-1">For: {med.indication}</p>}
                {med.max_prn_dose_24h && <p className="text-xs text-amber-700 mt-1 font-medium">Max dose: {med.max_prn_dose_24h}</p>}
                {med.is_controlled_drug && <p className="text-xs text-[#163A70] mt-1 font-medium">Controlled drug — use the witnessed CD workflow.</p>}
                {protocolComplete ? (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Minimum {med.prn_min_interval_minutes} min · max {med.prn_max_administrations_24h} administration(s)/24h · review after {med.prn_review_after_minutes} min
                  </p>
                ) : (
                  <p className="text-xs text-red-700 mt-1 font-medium">Protocol incomplete — administration is blocked until reviewed.</p>
                )}
                {todayCount > 0 && <p className="text-xs text-[#0F5C5E] mt-1 font-medium">{todayCount} recorded today</p>}
                {med.special_instructions && <p className="text-xs text-slate-400 mt-1 italic">{med.special_instructions}</p>}
              </div>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-xs text-slate-400">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <Pill className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No PRN administrations recorded yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(e => {
            const pendingOutcome = !e.outcome || e.outcome === 'not_assessed';
            const reviewReady = !e.review_due_at || new Date(e.review_due_at) <= new Date();
            return (
              <div key={e.id} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Pill className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{e.medication_name} — {e.dose_given}</p>
                  <p className="text-xs text-slate-600">Given for: {e.indication}</p>
                  {pendingOutcome ? (
                    <p className="text-xs text-amber-700 mt-1">
                      Effectiveness pending{e.review_due_at ? ` · review due ${new Date(e.review_due_at).toLocaleString('en-GB')}` : ''}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-600 mt-1">
                      Outcome: {String(e.outcome).replace(/_/g, ' ')}
                      {e.outcome_notes ? ` — ${e.outcome_notes}` : ''}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">{new Date(e.administered_at).toLocaleString('en-GB')} · {e.carer_name}</p>
                </div>
                {canRecord && pendingOutcome && (
                  reviewReady ? (
                    <Button size="sm" variant="outline" onClick={() => {
                      setOutcomeEntry(e);
                      setOutcomeForm({ outcome: '', notes: '' });
                    }}>
                      Record outcome
                    </Button>
                  ) : (
                    <span className="text-[10px] font-medium text-amber-700">Review not due yet</span>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {recordOpen && (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4" onClick={() => setRecordOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0F5C5E] to-[#163A70] rounded-t-2xl px-6 py-5 text-white">
              <p className="text-xs font-medium text-white/70 uppercase tracking-wider">PRN Administration</p>
              <h2 className="text-lg font-bold">Record As-Required Medication</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Medication *</label>
                <select className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  value={prnForm.medication_id} onChange={e => {
                    const m = recordablePrnMeds.find(x => x.id === e.target.value);
                    setPrnForm(current => ({ ...current, medication_id: e.target.value, dose_given: m?.dose || '' }));
                  }}>
                  <option value="">Select PRN medication</option>
                  {recordablePrnMeds.map(m => <option key={m.id} value={m.id}>{m.medication_name} {m.dose}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Date & time given *</label>
                <input type="datetime-local" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  value={prnForm.administered_at} onChange={e => setPrnForm(current => ({ ...current, administered_at: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Dose given *</label>
                <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  value={prnForm.dose_given} onChange={e => setPrnForm(current => ({ ...current, dose_given: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Why was it needed? *</label>
                <textarea rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none"
                  value={prnForm.indication} onChange={e => setPrnForm(current => ({ ...current, indication: e.target.value }))} placeholder="Describe the symptom / reason before giving" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Administration note</label>
                <textarea rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none"
                  value={prnForm.notes} onChange={e => setPrnForm(current => ({ ...current, notes: e.target.value }))} />
              </div>
              <p className="text-xs text-amber-700">Effectiveness is intentionally recorded afterwards, not guessed at the time of administration.</p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <Button variant="outline" onClick={() => setRecordOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleRecord} disabled={saving} className="flex-1 bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
                {saving ? 'Recording…' : 'Record administration'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {outcomeEntry && (
        <div className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/40 p-4" onClick={() => setOutcomeEntry(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">PRN effectiveness follow-up</p>
              <h2 className="text-lg font-bold text-slate-900">{outcomeEntry.medication_name}</h2>
              <p className="text-xs text-slate-600 mt-1">Originally given for: {outcomeEntry.indication}</p>
            </div>
            <select className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              value={outcomeForm.outcome} onChange={e => setOutcomeForm(current => ({ ...current, outcome: e.target.value }))}>
              <option value="">Select outcome</option>
              <option value="effective">Effective — symptom relieved</option>
              <option value="partially_effective">Partially effective</option>
              <option value="no_effect">No effect</option>
            </select>
            <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none"
              value={outcomeForm.notes} onChange={e => setOutcomeForm(current => ({ ...current, notes: e.target.value }))} placeholder="Follow-up observation / action taken" />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setOutcomeEntry(null)} className="flex-1">Cancel</Button>
              <Button onClick={handleOutcome} disabled={saving} className="flex-1 bg-[#0F5C5E] text-white">
                {saving ? 'Saving…' : 'Save outcome'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function MedicationMAR({ serviceUserId, serviceUserName, readOnly = false }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'manager', 'super_admin', 'branch_manager', 'superadmin'].includes(user?.role);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [activeTab, setActiveTab] = useState('mar'); // mar | prn | medications | controlled-drugs
  const [marData, setMarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [hasCompetency, setHasCompetency] = useState(false);

  const [recordCell, setRecordCell] = useState(null);
  const [medDialogOpen, setMedDialogOpen] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [discontinueMed, setDiscontinueMed] = useState(null);
  const [discontinueReason, setDiscontinueReason] = useState('');
  const [discontinuing, setDiscontinuing] = useState(false);
  const [showDiscontinued, setShowDiscontinued] = useState(false);

  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
  const today = now.toISOString().slice(0, 10);
  const todayDay = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  const loadMAR = useCallback(async () => {
    if (!serviceUserId) return;
    setLoading(true);
    try {
      const [marRes, summaryRes] = await Promise.all([
        axios.get(`${API}/service-users/${serviceUserId}/mar-chart`, {
          params: { year, month },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/service-users/${serviceUserId}/medication-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setMarData(marRes.data);
      setSummary(summaryRes.data);
      // Check competency via backend
      setHasCompetency(isAdmin || false);
    } catch {
      toast.error('Could not load medication records');
    } finally {
      setLoading(false);
    }
  }, [serviceUserId, year, month, token, isAdmin]);

  useEffect(() => { loadMAR(); }, [loadMAR]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const canRecord = !readOnly && (isAdmin || hasCompetency);

  const handleDiscontinue = async () => {
    if (!discontinueReason.trim()) { toast.error('Reason is required'); return; }
    setDiscontinuing(true);
    try {
      await axios.post(
        `${API}/service-users/${serviceUserId}/medications/${discontinueMed.id}/discontinue`,
        { reason: discontinueReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Medication discontinued');
      setDiscontinueMed(null);
      setDiscontinueReason('');
      loadMAR();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not discontinue');
    } finally {
      setDiscontinuing(false);
    }
  };

  const days = marData ? marData.days_in_month : 31;

  // ─ Summary cards ─
  const SummaryCards = summary ? (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {[
        { label: 'Active medications', value: summary.total_active, icon: Pill, color: 'text-[#0F5C5E] bg-[#D9F1F4]/40' },
        { label: 'PRN medications', value: summary.prn_count, icon: Clock, color: 'text-amber-700 bg-amber-50' },
        { label: 'Controlled drugs', value: summary.cd_count, icon: Shield, color: 'text-[#163A70] bg-blue-50' },
        { label: '7-day adherence', value: null, icon: TrendingUp, color: 'text-[#1F8F5F] bg-[#D8E6D5]',
          custom: <AdherencePill pct={summary.adherence_7d} /> },
      ].map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.color}`}>
            <c.icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{c.label}</p>
            {c.custom || <p className="text-xl font-bold text-slate-900">{c.value}</p>}
          </div>
        </div>
      ))}
    </div>
  ) : null;

  // ─ No competency warning ─
  const CompetencyWarning = !canRecord && !isAdmin && !readOnly ? (
    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
      <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-amber-800">Medication Administration Competency required</p>
        <p className="text-xs text-amber-700">You can view the MAR but cannot record administrations. Please speak to your manager about completing the medication competency assessment.</p>
      </div>
    </div>
  ) : null;

  // ─ MAR Grid ─
  const MARGrid = marData && (
    <div className="space-y-4">
      {/* Month navigator */}
      <div className="flex items-center justify-between">
        <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ChevronLeft className="h-4 w-4 text-slate-600" />
        </button>
        <h3 className="text-sm font-semibold text-slate-800">{MONTH_NAMES[month - 1]} {year}</h3>
        <button onClick={nextMonth} disabled={isCurrentMonth} className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors">
          <ChevronRight className="h-4 w-4 text-slate-600" />
        </button>
      </div>

      {/* Adherence bar */}
      {marData.summary && marData.summary.total_expected > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-600">Monthly adherence</span>
            <AdherencePill pct={marData.summary.adherence_pct} />
          </div>
          {Number.isFinite(Number(marData.summary.adherence_pct)) ? (
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#0F5C5E] rounded-full transition-all" style={{ width: `${Number(marData.summary.adherence_pct)}%` }} />
            </div>
          ) : (
            <p className="text-xs text-amber-700">
              CareOps cannot calculate adherence until every medication has a complete supported schedule.
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
            <span>Expected due: {marData.summary.total_expected ?? 0}</span>
            <span>Recorded: {marData.summary.recorded ?? 0}</span>
            <span>Missing: {marData.summary.missing ?? 0}</span>
          </div>
          {marData.summary.concerns > 0 && (
            <p className="text-xs text-red-600 mt-1.5 font-medium flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />{marData.summary.concerns} concern{marData.summary.concerns !== 1 ? 's' : ''} this month
            </p>
          )}
        </div>
      )}

      {marData.medications.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
          <Pill className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700 mb-1">No regular medications on the prescription list</p>
          <p className="text-xs text-slate-500 mb-4">Add medications using the button above to begin recording MAR entries.</p>
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditingMed(null); setMedDialogOpen(true); }} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" />Add first medication
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white">
          <table className="min-w-max w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-semibold text-slate-600 min-w-[220px] border-r border-slate-200">
                  Medication
                </th>
                {Array.from({ length: days }, (_, i) => i + 1).map(d => {
                  const dow = getDayOfWeek(year, month, d);
                  const isWknd = dow === 0 || dow === 6;
                  const isTdy = isCurrentMonth && d === todayDay;
                  return (
                    <th key={d} className={`px-1 py-2 text-center min-w-[30px] ${isWknd ? 'bg-slate-100' : ''} ${isTdy ? 'bg-[#D9F1F4]/40' : ''}`}>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[8px] text-slate-400 font-normal">{DAY_ABBR[dow]}</span>
                        <span className={`text-xs font-semibold ${isTdy ? 'text-[#0F5C5E] bg-[#0F5C5E] text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px]' : 'text-slate-600'}`}>
                          {d}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {marData.medications.map(med => {
                const medEntries = marData.entries.filter(e => e.medication_id === med.id);
                const concerns = medEntries.filter(e => ['refused','error'].includes(e.status)).length;
                return (
                  <tr key={med.id} className="group hover:bg-slate-50/50 transition-colors">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50/50 px-4 py-2 border-r border-slate-200 min-w-[220px]">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-slate-900 truncate">{med.medication_name}</span>
                            {med.is_controlled_drug && <Badge className="bg-[#163A70]/10 text-[#163A70] text-[9px] px-1 py-0 h-4">CD</Badge>}
                            {med.covert && <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1 py-0 h-4">Covert</Badge>}
                            {concerns > 0 && <Badge className="bg-red-100 text-red-700 text-[9px] px-1 py-0 h-4">{concerns}!</Badge>}
                          </div>
                          <p className="text-[10px] text-slate-500 mt-0.5">{med.dose} · {ROUTE_ABBR[med.route] || med.route} · {med.times?.map(fmtTime).join(', ')}</p>
                          {med.indication && <p className="text-[9px] text-slate-400 mt-0.5 truncate">For: {med.indication}</p>}
                        </div>
                        {isAdmin && (
                          <button onClick={() => { setEditingMed(med); setMedDialogOpen(true); }}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 transition-all flex-shrink-0">
                            <Edit3 className="h-3 w-3 text-slate-500" />
                          </button>
                        )}
                      </div>
                      {/* Time slots */}
                      {med.times?.length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {med.times.map(t => (
                            <span key={t} className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-mono">{fmtTime(t)}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    {Array.from({ length: days }, (_, i) => i + 1).map(d => {
                      const dow = getDayOfWeek(year, month, d);
                      const isWknd = dow === 0 || dow === 6;
                      const isTdy = isCurrentMonth && d === todayDay;
                      const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                      const dayEntries = marData.entries.filter(e => e.medication_id === med.id && e.administration_date === dateStr);
                      const isActuallyPast = dateStr < today;
                      const isActuallyToday = dateStr === today;
                      const isActuallyFuture = dateStr > today;
                      return (
                        <td key={d} className={`px-0.5 py-2 text-center align-top ${isWknd ? 'bg-slate-50' : ''} ${isTdy ? 'bg-[#D9F1F4]/20' : ''}`}>
                          <div className="flex flex-col items-center gap-0.5">
                            {(med.times || []).map(t => {
                              const entry = dayEntries.find(e => e.scheduled_time === t);
                              const scheduleKnown = !Array.isArray(med.schedule_dates) || med.schedule_dates.includes(dateStr);
                              if (!scheduleKnown && !entry) {
                                return (
                                  <div
                                    key={t}
                                    className="w-7 h-7 rounded flex items-center justify-center bg-slate-50 text-slate-200 text-[9px] font-medium select-none"
                                    title="Not scheduled on this date"
                                  >
                                    —
                                  </div>
                                );
                              }
                              return (
                                <MARCell
                                  key={t}
                                  entry={entry}
                                  medication={med}
                                  day={d}
                                  time={t}
                                  isPast={isActuallyPast}
                                  isToday={isActuallyToday}
                                  isFuture={isActuallyFuture || !scheduleKnown}
                                  onRecord={(c) => setRecordCell({ ...c, year, month })}
                                  canRecord={canRecord}
                                />
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        {Object.entries(STATUS_CONFIG).map(([k, v]) => (
          <span key={k} className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border ${v.bg} ${v.border}`}>
            <span className={`font-bold text-[10px] ${v.text}`}>{v.code}</span>
            <span>{v.label}</span>
          </span>
        ))}
        <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-red-200 bg-red-50/30">
          <span className="text-red-300 font-bold text-[10px]">?</span>
          <span>Missed / not recorded</span>
        </span>
      </div>
    </div>
  );

  // ─ Medications list ─
  const MedicationsList = marData && (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Prescription List</h3>
          <p className="text-xs text-slate-500">All current and discontinued medications</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowDiscontinued(s => !s)}>
            {showDiscontinued ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
            {showDiscontinued ? 'Hide discontinued' : 'Show discontinued'}
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditingMed(null); setMedDialogOpen(true); }} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" />Add medication
            </Button>
          )}
        </div>
      </div>

      {[...marData.medications, ...(showDiscontinued && marData.discontinued_medications ? marData.discontinued_medications : [])].length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <Pill className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No medications on file</p>
        </div>
      ) : (
        <div className="space-y-3">
          {marData.medications.map(med => (
            <MedicationCard key={med.id} med={med} isAdmin={isAdmin}
              onEdit={() => { setEditingMed(med); setMedDialogOpen(true); }}
              onDiscontinue={() => setDiscontinueMed(med)} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Pill className="h-5 w-5 text-[#0F5C5E]" />
            Medication Administration Record (eMAR)
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            CQC-compliant electronic MAR · Reg 12(2)(g) Health & Social Care Act 2008
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadMAR} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" title="Refresh">
            <RefreshCw className={`h-4 w-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditingMed(null); setMedDialogOpen(true); }} className="bg-[#0F5C5E] hover:bg-[#0c4a4c] text-white">
              <Plus className="h-3.5 w-3.5 mr-1" />Add medication
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      {SummaryCards}

      {/* Competency warning */}
      {CompetencyWarning}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        {[
          { id: 'mar', label: 'MAR Chart' },
          { id: 'prn', label: 'PRN Log' },
          { id: 'medications', label: 'Prescription List' },
          ...(isAdmin && (summary?.cd_count || 0) > 0
            ? [{ id: 'controlled-drugs', label: 'CD Register' }]
            : []),
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
              activeTab === t.id
                ? 'bg-white text-[#0F5C5E] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 rounded-full border-2 border-[#0F5C5E] border-t-transparent animate-spin" />
            <p className="text-xs text-slate-400">Loading medication records…</p>
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'mar' && MARGrid}
          {activeTab === 'prn' && marData && (
            <PRNHistory
              serviceUserId={serviceUserId}
              prnMeds={marData.prn_medications || []}
              canRecord={canRecord}
            />
          )}
          {activeTab === 'medications' && MedicationsList}
          {activeTab === 'controlled-drugs' && isAdmin && (
            <ControlledDrugRegister
              serviceUserId={serviceUserId}
              readOnly={readOnly}
            />
          )}
        </>
      )}

      {/* Record/View dialog */}
      <RecordDialog
        open={!!recordCell}
        onClose={() => setRecordCell(null)}
        cell={recordCell}
        onSaved={loadMAR}
        serviceUserId={serviceUserId}
      />

      {/* Add/Edit medication dialog */}
      <MedicationDialog
        open={medDialogOpen}
        onClose={() => { setMedDialogOpen(false); setEditingMed(null); }}
        onSaved={loadMAR}
        serviceUserId={serviceUserId}
        editingMed={editingMed}
      />

      {/* Discontinue confirmation */}
      {discontinueMed && (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/40 p-4" onClick={() => setDiscontinueMed(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">Discontinue medication?</h3>
                <p className="text-sm text-slate-600 mt-1">{discontinueMed.medication_name} · {discontinueMed.dose}</p>
                <p className="text-xs text-slate-500 mt-1">This will remove it from the active MAR. All historical entries are preserved.</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Reason for stopping *</label>
              <textarea rows={3} value={discontinueReason} onChange={e => setDiscontinueReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none focus:ring-1 focus:ring-[#0F5C5E]"
                placeholder="e.g. GP instructions, course completed, side effects — specify clearly" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDiscontinueMed(null)} className="flex-1">Cancel</Button>
              <Button onClick={handleDiscontinue} disabled={discontinuing || !discontinueReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                {discontinuing ? 'Stopping…' : 'Discontinue medication'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Medication card for the prescription list */
function MedicationCard({ med, isAdmin, onEdit, onDiscontinue }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`rounded-xl border shadow-sm overflow-hidden ${med.is_active ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/50 opacity-70'}`}>
      <div className="flex items-start gap-3 p-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          med.is_controlled_drug ? 'bg-[#163A70]/10' : med.is_prn ? 'bg-amber-100' : 'bg-[#D9F1F4]/50'
        }`}>
          {med.is_controlled_drug ? <Shield className="h-5 w-5 text-[#163A70]" /> :
           med.is_prn ? <Clock className="h-5 w-5 text-amber-600" /> :
           <Pill className="h-5 w-5 text-[#0F5C5E]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">{med.medication_name}</span>
            {!med.is_active && <Badge className="bg-slate-100 text-slate-500 text-xs">Discontinued</Badge>}
            {med.is_controlled_drug && <Badge className="bg-[#163A70]/10 text-[#163A70] text-xs">CD {med.cd_schedule}</Badge>}
            {med.is_prn && <Badge className="bg-amber-100 text-amber-700 text-xs">PRN</Badge>}
            {med.covert && <Badge className="bg-amber-100 text-amber-700 text-xs">Covert</Badge>}
          </div>
          <p className="text-xs text-slate-600 mt-0.5">{med.dose} · {med.form} · {med.route}</p>
          {med.indication && <p className="text-xs text-slate-500">For: {med.indication}</p>}
          <p className="text-xs text-slate-400 mt-0.5">{med.frequency} · From {med.start_date}</p>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => setExpanded(e => !e)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <Info className="h-4 w-4 text-slate-400" />
          </button>
          {isAdmin && med.is_active && (
            <>
              <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <Edit3 className="h-4 w-4 text-slate-400" />
              </button>
              <button onClick={onDiscontinue} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                <XCircle className="h-4 w-4 text-red-400" />
              </button>
            </>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-0 grid grid-cols-2 gap-2 text-xs border-t border-slate-100">
          {[
            ['Generic name', med.generic_name],
            ['Prescriber', med.prescriber],
            ['GP surgery', med.gp_name],
            ['Administration times', med.times?.map(fmtTime).join(', ')],
            ['Start date', med.start_date],
            ['End / review', med.end_date],
            ['Special instructions', med.special_instructions],
            ...(med.is_prn ? [['Max dose in 24h', med.max_prn_dose_24h]] : []),
            ...(med.is_controlled_drug ? [['CD schedule', med.cd_schedule]] : []),
            ...(!med.is_active ? [['Discontinued', med.discontinued_at && new Date(med.discontinued_at).toLocaleDateString('en-GB')], ['Reason', med.discontinued_reason]] : []),
          ].filter(([, v]) => v).map(([k, v]) => (
            <div key={k}>
              <p className="text-slate-400 font-medium">{k}</p>
              <p className="text-slate-700">{v}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
